import {
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  styled,
  Typography,
} from '@healthinal/ui';
import isEmail from 'is-email';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useCheckAddressDuplicate } from '../../../api/created/addressDuplicate.ts';
import { AddressDto, AddressDtoAddressType } from '../../../api/generated/model';
import { FormActionButtons } from '../../../components/form/FormActionButtons.tsx';
import { PostalCodeFields } from '../../../components/PostalCodeFields.tsx';
import { isPhoneNumberValid } from '../../../utils/phoneNumber.ts';
import { getStringOrUndefined } from '../../../utils/string.ts';

interface FormValues {
  addressType: AddressDtoAddressType;
  organization: string;
  phone: string;
  address: string;
  zip: string;
  city: string;
  canton: string;
  email: string;
  homepage: string;
  zusatz: string;
}

interface CreateAddressDialogProps {
  open: boolean;
  onClose: (addressDto?: AddressDto) => void;
  selectedAddressTypes: AddressDtoAddressType[] | null;
  canChangeAddressType: boolean;
  address?: AddressDto | null;
  isLoading?: boolean;
}

const DUPLICATE_CHECK_DEBOUNCE_MS = 700;

const EMPTY_FORM_VALUES: FormValues = {
  addressType: 'ACCOMMODATION_ASYLUM',
  organization: '',
  phone: '',
  address: '',
  zip: '',
  city: '',
  canton: '',
  email: '',
  homepage: '',
  zusatz: '',
};

function getAddressZusatz(address: AddressDto): string {
  const extended = address as AddressDto & { zusatz?: string | null };

  return extended.zusatz ?? address.additionalInformation ?? '';
}

function addressToFormValues(address: AddressDto): FormValues {
  const extended = address as AddressDto & { canton?: string | null };

  return {
    addressType: address.addressType ?? 'ACCOMMODATION_ASYLUM',
    organization: address.organization ?? '',
    phone: address.phone ?? '',
    address: address.address ?? '',
    zip: address.zip != null ? String(address.zip) : '',
    city: address.city ?? '',
    canton: extended.canton ?? '',
    email: address.email ?? '',
    homepage: address.homepage ?? '',
    zusatz: getAddressZusatz(address),
  };
}

export function CreateAddressDialog(props: CreateAddressDialogProps) {
  const { open, onClose, address, isLoading } = props;
  const isEditing = !!address?.id;

  const {
    register,
    control,
    reset,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors, isDirty: isFormDirty },
  } = useForm<FormValues>({
    defaultValues: EMPTY_FORM_VALUES,
  });

  const organizationRegistration = register('organization', {
    required: true,
    validate: (organization) => !!organization.trim(),
  });

  const [duplicateCheckInstitution, setDuplicateCheckInstitution] = useState('');
  const organization = watch('organization');

  const { data: isDuplicateInstitution } = useCheckAddressDuplicate(
    duplicateCheckInstitution,
    open && !!duplicateCheckInstitution,
  );

  const isDuplicateOrganization =
    !!isDuplicateInstitution && (!isEditing || duplicateCheckInstitution.trim() !== address?.organization?.trim());

  const shouldValidateDuplicate = (organizationName: string) => {
    const trimmed = organizationName.trim();

    if (!trimmed) return false;

    return !isEditing || trimmed !== address?.organization?.trim();
  };

  const handleOnClose = () => {
    reset(EMPTY_FORM_VALUES);
    onClose();
  };

  useEffect(() => {
    if (open) {
      reset(address ? addressToFormValues(address) : EMPTY_FORM_VALUES);
      setDuplicateCheckInstitution('');
    }
  }, [open, address, reset]);

  useEffect(() => {
    if (!open) return;

    const trimmed = organization.trim();

    if (!shouldValidateDuplicate(trimmed)) {
      setDuplicateCheckInstitution('');
      return;
    }

    const timeout = setTimeout(() => setDuplicateCheckInstitution(trimmed), DUPLICATE_CHECK_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [organization, open, isEditing, address?.organization]);

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 800 }}>
        <ModalClose />
        <Typography level="h4">{isEditing ? 'Diese Einrichtung bearbeiten' : 'Institution erstellen'}</Typography>
        <Divider />

        <form
          onSubmit={(event) => {
            event.stopPropagation();

            void handleSubmit(async (data) => {
              const zip = data.zip ? Number(data.zip) : null;

              if (isEditing && address?.id) {
                onClose({
                  id: address.id,
                  addressType: address.addressType ?? data.addressType,
                  address: getStringOrUndefined(data.address) ?? null,
                  city: getStringOrUndefined(data.city) ?? null,
                  canton: getStringOrUndefined(data.canton) ?? null,
                  organization: getStringOrUndefined(data.organization) ?? null,
                  phone: getStringOrUndefined(data.phone) ?? null,
                  zip,
                  email: getStringOrUndefined(data.email) ?? null,
                  homepage: getStringOrUndefined(data.homepage) ?? null,
                  zusatz: getStringOrUndefined(data.zusatz) ?? null,
                  isActive: address.isActive,
                } as AddressDto);
              } else {
                onClose({
                  addressType: data.addressType,
                  address: getStringOrUndefined(data.address) ?? null,
                  city: getStringOrUndefined(data.city) ?? null,
                  canton: getStringOrUndefined(data.canton) ?? null,
                  organization: getStringOrUndefined(data.organization) ?? null,
                  phone: getStringOrUndefined(data.phone) ?? null,
                  zip,
                  email: getStringOrUndefined(data.email) ?? null,
                  homepage: getStringOrUndefined(data.homepage) ?? null,
                  zusatz: getStringOrUndefined(data.zusatz) ?? null,
                  isActive: true,
                } as AddressDto);
              }
            })(event);
          }}>
          <Stack gap={2}>
            <FormGrid>
              <FormControl error={!!errors.organization || isDuplicateOrganization}>
                <FormLabel>
                  <Typography>
                    Institution <span style={{ color: 'var(--joy-palette-danger-500)' }}>*</span>
                  </Typography>
                </FormLabel>
                <Input
                  {...organizationRegistration}
                  onBlur={(event) => {
                    void organizationRegistration.onBlur(event);
                    setDuplicateCheckInstitution(event.target.value);
                  }}
                />
                {errors.organization && <FormHelperText>Institution muss angegeben werden</FormHelperText>}
                {!errors.organization && isDuplicateOrganization && (
                  <FormHelperText>Diese Institution existiert bereits</FormHelperText>
                )}
              </FormControl>

              <FormControl error={!!errors.phone}>
                <FormLabel>
                  <Typography>Telefon Nr.</Typography>
                </FormLabel>
                <Input
                  {...register('phone', {
                    validate: (phone) => !phone || isPhoneNumberValid(phone),
                  })}
                  type="tel"
                />
                {errors.phone && <FormHelperText>Diese Telefon Nr. ist nicht korrekt</FormHelperText>}
              </FormControl>

              <FormControl error={!!errors.email}>
                <FormLabel>
                  <Typography>E-Mail</Typography>
                </FormLabel>
                <Input
                  {...register('email', {
                    validate: (email) => !email || isEmail(email),
                  })}
                />
                {errors.email && <FormHelperText>Diese E-Mail-Adresse ist nicht korrekt</FormHelperText>}
              </FormControl>

              <FormControl>
                <FormLabel>
                  <Typography>Homepage</Typography>
                </FormLabel>
                <Input {...register('homepage')} />
              </FormControl>

              <FormControl>
                <FormLabel>
                  <Typography>Strasse</Typography>
                </FormLabel>
                <Input {...register('address')} />
              </FormControl>

              <FormControl>
                <FormLabel>
                  <Typography>Zusatz</Typography>
                </FormLabel>
                <Input {...register('zusatz')} />
              </FormControl>

              <PostalCodeFields<FormValues>
                control={control}
                setValue={setValue}
                getValues={getValues}
                zipName="zip"
                cityName="city"
                cantonName="canton"
                zipLabel="PLZ"
                cityLabel="Ort"
                cantonLabel="Kanton"
                enabled={open}
                variant="outlined"
              />
            </FormGrid>

            <FormActionButtons
              onAbortClick={handleOnClose}
              disableSubmit={!isFormDirty}
              isLoading={isLoading}
            />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}

const FormGrid = styled('div')`
  display: grid;
  gap: 0.75rem 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
`;
